// Stories on the web: rings from people you follow (plus discover), and a full-screen viewer with progress bars,
// tap/arrow-key navigation, pause (hold / space), reply by DM and owner view counts.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Eye, Pause, Play, Send, X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

const IMAGE_MS = 5000;
const ago = (s) => { const m = Math.max(1, Math.round((Date.now() - new Date(s).getTime()) / 60000)); return m < 60 ? m + "m" : Math.round(m / 60) + "h"; };

function Ring({ r, onOpen }) {
  const a = r.author || {};
  return (
    <button type="button" onClick={onOpen} className="flex w-20 shrink-0 flex-col items-center gap-1 text-xs" aria-label={"Watch stories from " + (a.display_name || a.handle)}>
      <span className={"rounded-full p-[3px] " + (r.unseen ? (r.close_friends ? "bg-emerald-500" : "bg-gradient-to-tr from-amber-400 via-pink-500 to-fuchsia-600") : "bg-muted")}>
        <span className="block rounded-full bg-background p-[2px]">
          {a.avatar_url ? <img src={a.avatar_url} alt="" className="h-16 w-16 rounded-full object-cover" /> : <span className="grid h-16 w-16 place-items-center rounded-full bg-muted text-lg font-bold">{(a.display_name || a.handle || "?")[0]}</span>}
        </span>
      </span>
      <span className="w-full truncate text-center">{r.is_me ? "Your story" : (a.display_name || "@" + a.handle)}</span>
    </button>
  );
}

function Viewer({ rings, start, onClose }) {
  const [ri, setRi] = useState(start);
  const [si, setSi] = useState(() => { const it = rings[start].items; const u = it.findIndex((s) => !s.seen); return u < 0 ? 0 : u; });
  const [paused, setPaused] = useState(false);
  const [pct, setPct] = useState(0);
  const [reply, setReply] = useState("");
  const vid = useRef(null);
  const ring = rings[ri];
  const story = ring && ring.items[si];
  const next = useCallback(() => {
    setPct(0);
    if (si < ring.items.length - 1) setSi(si + 1);
    else if (ri < rings.length - 1) { setRi(ri + 1); setSi(0); }
    else onClose();
  }, [si, ri, ring, rings.length, onClose]);
  const prev = useCallback(() => {
    setPct(0);
    if (si > 0) setSi(si - 1);
    else if (ri > 0) { setRi(ri - 1); setSi(rings[ri - 1].items.length - 1); }
  }, [si, ri, rings]);
  useEffect(() => { if (story && !story.seen) { api("/stories/" + story.id + "/view", { method: "POST" }).catch(() => {}); story.seen = true; } }, [story]);
  useEffect(() => {
    if (!story || story.media.type === "video" || paused) return undefined;
    const t0 = Date.now() - (pct / 100) * IMAGE_MS;
    const h = setInterval(() => { const p = Math.min(100, ((Date.now() - t0) / IMAGE_MS) * 100); setPct(p); if (p >= 100) { clearInterval(h); next(); } }, 50);
    return () => clearInterval(h);
  }, [story, paused, next]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const v = vid.current; if (!v) return; if (paused) v.pause(); else v.play().catch(() => {}); }, [paused, story]);
  useEffect(() => {
    const k = (e) => {
      if (e.target.tagName === "INPUT") return;
      if (e.key === "ArrowRight") next(); else if (e.key === "ArrowLeft") prev(); else if (e.key === "Escape") onClose();
      else if (e.key === " ") { e.preventDefault(); setPaused((p) => !p); }
    };
    window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k);
  }, [next, prev, onClose]);
  if (!story) return null;
  const a = ring.author || {};
  const send = async (e) => {
    e.preventDefault(); const text = reply.trim(); if (!text) return;
    try { await api("/stories/" + story.id + "/reply", { method: "POST", body: { text } }); setReply(""); toast.success("Reply sent"); } catch (err) { toast.error(err.message || "Couldn't send"); }
  };
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/95" role="dialog" aria-modal="true" aria-label={"Stories from " + (a.display_name || a.handle)}>
      <button type="button" onClick={onClose} className="absolute right-4 top-4 rounded-full p-2 text-white hover:bg-white/10" aria-label="Close stories"><X className="h-6 w-6" /></button>
      <button type="button" onClick={prev} className="absolute left-4 hidden rounded-full bg-white/10 p-2 text-white md:block" aria-label="Previous"><ChevronLeft className="h-6 w-6" /></button>
      <button type="button" onClick={next} className="absolute right-4 hidden rounded-full bg-white/10 p-2 text-white md:block" aria-label="Next"><ChevronRight className="h-6 w-6" /></button>
      <div className="relative aspect-[9/16] h-full max-h-[92vh] w-auto max-w-full overflow-hidden rounded-2xl bg-neutral-900">
        <div className="absolute inset-x-2 top-2 z-10 flex gap-1">
          {ring.items.map((s, i) => <span key={s.id} className="h-0.5 flex-1 overflow-hidden rounded bg-white/30"><span className="block h-full bg-white" style={{ width: (i < si ? 100 : i === si ? pct : 0) + "%" }} /></span>)}
        </div>
        <div className="absolute inset-x-3 top-5 z-10 flex items-center gap-2 text-sm text-white">
          {a.avatar_url ? <img src={a.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover" /> : null}
          <Link to={"/creators/" + (a.handle || "")} className="font-semibold hover:underline">{a.display_name || "@" + a.handle}</Link>
          <span className="text-white/70">{ago(story.created_at)}</span>
          <button type="button" className="ml-auto p-1" onClick={() => setPaused(!paused)} aria-label={paused ? "Play" : "Pause"}>{paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}</button>
        </div>
        {story.media.type === "video" ? (
          <video key={story.id} ref={vid} src={story.media.url} poster={story.media.poster || undefined} className="h-full w-full object-cover" autoPlay playsInline
            onTimeUpdate={(e) => { const v = e.currentTarget; if (v.duration) setPct((v.currentTime / v.duration) * 100); }} onEnded={next} />
        ) : <img key={story.id} src={story.media.url} alt={"Story from " + (a.display_name || a.handle)} className="h-full w-full object-cover" />}
        <div className="absolute inset-0 flex" onMouseDown={() => setPaused(true)} onMouseUp={() => setPaused(false)} onTouchStart={() => setPaused(true)} onTouchEnd={() => setPaused(false)} style={{ bottom: 72, top: 64 }}>
          <button type="button" className="h-full w-1/3" onClick={prev} aria-label="Previous story" tabIndex={-1} />
          <button type="button" className="h-full w-2/3" onClick={next} aria-label="Next story" tabIndex={-1} />
        </div>
        <div className="absolute inset-x-3 bottom-3 z-10">
          {story.is_owner ? (
            <p className="flex items-center gap-1 text-sm text-white"><Eye className="h-4 w-4" aria-hidden="true" />{story.views || 0} views</p>
          ) : (
            <form onSubmit={send} className="flex items-center gap-2">
              <input value={reply} onChange={(e) => setReply(e.target.value)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} maxLength={500}
                placeholder={"Reply to " + (a.display_name || a.handle)} aria-label="Reply" className="flex-1 rounded-full border border-white/40 bg-transparent px-4 py-2 text-sm text-white placeholder:text-white/60 outline-none" />
              <button type="submit" className="rounded-full p-2 text-white" aria-label="Send reply"><Send className="h-5 w-5" /></button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Stories() {
  const { userId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { document.title = "Stories · Fable+"; }, []);
  useEffect(() => {
    api("/stories/feed").then(async (d) => {
      let all = [...(d.rings || []), ...(d.discover || [])];
      if (userId && !all.some((r) => r.user_id === userId)) {
        try { const u = await api("/stories/user/" + userId); if (u.items && u.items.length) all = [{ ...u, user_id: userId }, ...all]; } catch (e) { /* not visible */ }
      }
      setData({ ...d, all });
      if (userId) { const i = all.findIndex((r) => r.user_id === userId); if (i >= 0) setOpen(i); }
    }).catch(() => setData({ rings: [], discover: [], suggested: [], all: [] }));
  }, [userId]);
  const close = useCallback(() => { setOpen(null); if (userId) nav("/stories", { replace: true }); }, [userId, nav]);
  if (!data) return <main className="mx-auto max-w-5xl px-4 py-10"><Skeleton className="h-24 rounded-2xl" /></main>;
  const rings = data.all.filter((r) => r.items && r.items.length);
  const nFollow = (data.rings || []).filter((r) => r.items && r.items.length).length;
  return (
    <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6" data-testid="stories-page">
      <h1 className="text-3xl font-bold tracking-tight">Stories</h1>
      <p className="mt-1 text-sm text-muted-foreground">Gone after 24 hours. Use ← → to move, space to pause.</p>
      {rings.length === 0 ? <p className="mt-8 text-sm text-muted-foreground">No stories right now. Follow creators on Stage to see theirs here.</p> : (
        <>
          {nFollow ? <><h2 className="mt-8 text-sm font-bold uppercase tracking-wider text-muted-foreground">Following</h2>
            <div className="mt-3 flex gap-3 overflow-x-auto pb-2">{rings.slice(0, nFollow).map((r, i) => <Ring key={r.user_id} r={r} onOpen={() => setOpen(i)} />)}</div></> : null}
          {rings.length > nFollow ? <><h2 className="mt-8 text-sm font-bold uppercase tracking-wider text-muted-foreground">Discover</h2>
            <div className="mt-3 flex gap-3 overflow-x-auto pb-2">{rings.slice(nFollow).map((r, i) => <Ring key={r.user_id} r={r} onOpen={() => setOpen(nFollow + i)} />)}</div></> : null}
        </>
      )}
      {(data.suggested || []).length ? (
        <section className="mt-10"><h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Creators to follow</h2>
          <div className="mt-3 flex flex-wrap gap-3">{data.suggested.map((c) => <Link key={c.user_id} to={"/creators/" + c.handle} className="rounded-full border px-3 py-1.5 text-sm hover:bg-muted">{c.display_name || "@" + c.handle}</Link>)}</div>
        </section>
      ) : null}
      {open !== null && rings[open] ? <Viewer rings={rings} start={open} onClose={close} /> : null}
    </main>
  );
}
